export interface PrintBookPageCsvRow {
  rowNumber: number
  lessonId: number
  startPage: number
  pageCount: number
  endPage: number
  blockQuizStartPage?: number | null
}

export interface PrintBookPageCsvError {
  rowNumber: number
  code: 'EMPTY' | 'MISSING_HEADER' | 'INVALID_LESSON_ID' | 'UNKNOWN_LESSON' | 'INVALID_START_PAGE' | 'INVALID_PAGE_COUNT' | 'INVALID_BLOCK_QUIZ_START_PAGE' | 'BLOCK_QUIZ_PAGE_OUT_OF_RANGE' | 'DUPLICATE_LESSON' | 'OVERLAPPING_PAGE_RANGE'
  message: string
}

export interface PrintBookPageCsvParseResult {
  rows: PrintBookPageCsvRow[]
  errors: PrintBookPageCsvError[]
  hasErrors: boolean
}

const HEADER_ALIASES = {
  lessonId: ['lessonid', 'lesson_id', '레슨id', '레슨_id'],
  startPage: ['startpage', 'start_page', '시작페이지', '시작_페이지'],
  pageCount: ['pagecount', 'page_count', '페이지수', '페이지_수'],
  blockQuizStartPage: ['blockquizstartpage', 'block_quiz_start_page', '블록퀴즈시작페이지', '블록퀴즈_시작_페이지'],
}

function parseCsvLine(line: string): string[] {
  const cells: string[] = []
  let current = ''
  let quoted = false

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index]
    if (character === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"'
        index += 1
      } else {
        quoted = !quoted
      }
    } else if (character === ',' && !quoted) {
      cells.push(current.trim())
      current = ''
    } else {
      current += character
    }
  }
  cells.push(current.trim())
  return cells
}

function normalizeHeader(header: string) {
  return header.replace(/^\uFEFF/, '').trim().toLowerCase()
}

function findHeaderIndex(headers: string[], aliases: string[]) {
  return headers.findIndex((header) => aliases.includes(normalizeHeader(header)))
}

export function parsePrintBookPageCsv(input: string, validLessonIds: ReadonlySet<number>): PrintBookPageCsvParseResult {
  const physicalRows = input
    .split(/\r?\n/)
    .map((line, index) => ({ line, rowNumber: index + 1 }))
    .filter(({ line }) => line.trim().length > 0)

  if (physicalRows.length === 0) {
    const errors: PrintBookPageCsvError[] = [{ rowNumber: 1, code: 'EMPTY', message: 'CSV 내용이 비어 있습니다.' }]
    return { rows: [], errors, hasErrors: true }
  }

  const headers = parseCsvLine(physicalRows[0].line)
  const lessonIdIndex = findHeaderIndex(headers, HEADER_ALIASES.lessonId)
  const startPageIndex = findHeaderIndex(headers, HEADER_ALIASES.startPage)
  const pageCountIndex = findHeaderIndex(headers, HEADER_ALIASES.pageCount)
  const blockQuizStartPageIndex = findHeaderIndex(headers, HEADER_ALIASES.blockQuizStartPage)

  if (lessonIdIndex < 0 || startPageIndex < 0 || pageCountIndex < 0) {
    const errors: PrintBookPageCsvError[] = [{
      rowNumber: physicalRows[0].rowNumber,
      code: 'MISSING_HEADER',
      message: 'lessonId, startPage, pageCount 헤더가 필요합니다.',
    }]
    return { rows: [], errors, hasErrors: true }
  }

  const rows: PrintBookPageCsvRow[] = []
  const errors: PrintBookPageCsvError[] = []
  const firstRowByLessonId = new Map<number, number>()

  physicalRows.slice(1).forEach(({ line, rowNumber }) => {
    const cells = parseCsvLine(line)
    const lessonId = Number(cells[lessonIdIndex])
    const startPage = Number(cells[startPageIndex])
    const pageCount = Number(cells[pageCountIndex])
    const blockQuizStartPageCell = blockQuizStartPageIndex >= 0
      ? (cells[blockQuizStartPageIndex] ?? '').trim()
      : undefined
    const blockQuizStartPage = blockQuizStartPageCell === undefined || blockQuizStartPageCell === ''
      ? null
      : Number(blockQuizStartPageCell)
    let valid = true

    if (!Number.isInteger(lessonId) || lessonId < 1) {
      errors.push({ rowNumber, code: 'INVALID_LESSON_ID', message: 'lessonId는 1 이상의 정수여야 합니다.' })
      valid = false
    } else if (!validLessonIds.has(lessonId)) {
      errors.push({ rowNumber, code: 'UNKNOWN_LESSON', message: `현재 교재에 없는 lessonId입니다: ${lessonId}` })
      valid = false
    } else if (firstRowByLessonId.has(lessonId)) {
      errors.push({ rowNumber, code: 'DUPLICATE_LESSON', message: `lessonId ${lessonId}가 중복되었습니다.` })
      valid = false
    } else {
      firstRowByLessonId.set(lessonId, rowNumber)
    }

    if (!Number.isInteger(startPage) || startPage < 1) {
      errors.push({ rowNumber, code: 'INVALID_START_PAGE', message: 'startPage는 1 이상의 정수여야 합니다.' })
      valid = false
    }
    if (!Number.isInteger(pageCount) || pageCount < 1) {
      errors.push({ rowNumber, code: 'INVALID_PAGE_COUNT', message: 'pageCount는 1 이상의 정수여야 합니다.' })
      valid = false
    }
    if (blockQuizStartPageIndex >= 0 && blockQuizStartPage != null) {
      if (!Number.isInteger(blockQuizStartPage) || blockQuizStartPage < 1) {
        errors.push({
          rowNumber,
          code: 'INVALID_BLOCK_QUIZ_START_PAGE',
          message: 'blockQuizStartPage는 비우거나 1 이상의 정수로 입력해야 합니다.',
        })
        valid = false
      } else if (
        Number.isInteger(startPage) &&
        startPage >= 1 &&
        Number.isInteger(pageCount) &&
        pageCount >= 1 &&
        blockQuizStartPage > startPage + pageCount - 1
      ) {
        errors.push({
          rowNumber,
          code: 'BLOCK_QUIZ_PAGE_OUT_OF_RANGE',
          message: 'blockQuizStartPage는 해당 레슨의 페이지 범위 안이어야 합니다.',
        })
        valid = false
      } else if (Number.isInteger(startPage) && startPage >= 1 && blockQuizStartPage < startPage) {
        errors.push({
          rowNumber,
          code: 'BLOCK_QUIZ_PAGE_OUT_OF_RANGE',
          message: 'blockQuizStartPage는 해당 레슨의 페이지 범위 안이어야 합니다.',
        })
        valid = false
      }
    }

    if (valid) {
      rows.push({
        rowNumber,
        lessonId,
        startPage,
        pageCount,
        endPage: startPage + pageCount - 1,
        ...(blockQuizStartPageIndex >= 0 ? { blockQuizStartPage } : {}),
      })
    }
  })

  const sortedRows = [...rows].sort((left, right) => left.startPage - right.startPage)
  for (let index = 1; index < sortedRows.length; index += 1) {
    const previous = sortedRows[index - 1]
    const current = sortedRows[index]
    if (current.startPage <= previous.endPage) {
      errors.push({
        rowNumber: current.rowNumber,
        code: 'OVERLAPPING_PAGE_RANGE',
        message: `lessonId ${previous.lessonId}의 페이지 범위(${previous.startPage}-${previous.endPage})와 겹칩니다.`,
      })
    }
  }

  return { rows, errors, hasErrors: errors.length > 0 }
}
